import type { ReactNode } from 'react';
import { AnnouncementBar } from '../features/storefront/shell/components/announcement-bar.js';
import { SiteHeader } from '../features/storefront/shell/components/site-header.js';
import { SiteFooter } from '../features/storefront/shell/components/site-footer.js';
import { PromoPopup } from '../features/storefront/components/promo-popup.js';
import { useMergeGuestCartOnSignIn } from '../features/storefront/cart/hooks/use-cart.js';

/**
 * The storefront's global shell -- announcement bar, sticky header (nav,
 * search, cart/wishlist, account menu, mobile hamburger), the routed page
 * content, and the footer. `App.tsx`'s `StorefrontLayout` renders
 * `<AppShell><Outlet/></AppShell>` around every customer-facing route, so
 * this stays the single place that chrome is assembled. Works whether or
 * not the visitor is signed in -- everything auth-gated lives inside
 * `SiteHeader`/`MobileMenu` via Clerk's `SignedIn`/`SignedOut`.
 *
 * Also mounts `useMergeGuestCartOnSignIn` here (rather than inside
 * `SiteHeader` or a cart-specific component) so a sign-in that happens
 * from any page -- not just the cart page -- still triggers the
 * guest-cart merge exactly once.
 */
export function AppShell({ children }: { children: ReactNode }) {
  useMergeGuestCartOnSignIn();

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <AnnouncementBar />
      <SiteHeader />
      <main className="flex-1">{children}</main>
      <SiteFooter />
      <PromoPopup />
    </div>
  );
}
